<template>
  <div class="employees-module">
    <v-card outlined class="info-card">
      <v-card-title class="info-title d-flex align-center">
        <v-btn
          v-if="useHrDetailRoute || openInDashboard"
          variant="text"
          color="primary"
          class="mr-2"
          @click="handleBack"
        >
          <v-icon start size="18">mdi-arrow-left</v-icon>
          {{ t("app.actions.back") }}
        </v-btn>
        <v-icon size="18" class="mr-2">mdi-account-multiple-outline</v-icon>
        {{ t("employees.title") }}
        <template v-if="activeCompany">
          <v-chip
            class="ml-4"
            color="primary"
            prepend-icon="mdi-domain"
          >
            {{ activeCompany.company_name }}
          </v-chip>
        </template>
        <template v-else-if="companyFilter !== null">
          <v-chip
            class="ml-4"
            color="primary"
            prepend-icon="mdi-domain"
          >
            {{ t("employees.companyLabel") }} #{{ companyFilter }}
          </v-chip>
        </template>
        <v-spacer></v-spacer>
        <v-tooltip location="bottom">
          <template #activator="{ props }">
            <v-btn
              icon
              variant="text"
              v-bind="props"
              @click="toggleViewMode"
              :title="viewMode === 'cards' ? t('employees.actions.showTable') : t('employees.actions.showCards')"
            >
              <v-icon>{{ viewMode === "cards" ? "mdi-view-list" : "mdi-view-module" }}</v-icon>
            </v-btn>
          </template>
          {{ viewMode === 'cards' ? t('employees.actions.showTable') : t('employees.actions.showCards') }}
        </v-tooltip>
        <v-btn v-if="allowManagement" color="primary" @click="openAddDialog">
          <v-icon size="18" start>mdi-plus</v-icon>
          {{ t("employees.actions.add") }}
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <div class="controls-row">
          <template v-if="showCompanySelect">
            <v-select
              v-model="selectedCompanyId"
              :items="companySelectItems"
              item-title="label"
              item-value="value"
              :label="t('employees.filters.company')"
              prepend-inner-icon="mdi-domain"
              clearable
              hide-details
              class="company-select"
            ></v-select>
          </template>
            <v-text-field
              v-model="search"
              :label="t('employees.filters.search')"
              prepend-inner-icon="mdi-magnify"
              hide-details
              :class="['search-field', !showCompanySelect ? 'search-field--full' : '']"
          ></v-text-field>
        </div>

        <div v-if="viewMode === 'cards'" class="employees-grid">
          <v-row dense>
            <v-col
              v-for="employee in filteredEmployees"
              :key="employee.id"
              cols="12"
              sm="12"
              md="6"
              lg="4"
              xl="3"
            >
              <v-card class="employee-card" outlined @click="goToEmployeeDetails(employee)">
                <div class="employee-card__header">
                  <v-avatar color="#E0E7FF" size="48" class="employee-card__avatar">
                    {{ employeeInitials(employee) }}
                  </v-avatar>
                  <div>
                    <div class="employee-card__name">
                      {{ employee.first_name }} {{ employee.last_name }}
                    </div>
                    <div class="employee-card__position">
                      {{ employee.position || t("employees.labels.missingPosition") }}
                    </div>
                    <div class="employee-card__meta text-caption text-medium-emphasis">
                      {{ employee.company_name || t("employees.labels.notAvailable") }}
                      <template v-if="employee.department">
                        · {{ employee.department }}
                      </template>
                    </div>
                  </div>
                </div>

                <div class="badge-row">
                  <v-chip size="x-small" :color="employee.status === 1 ? 'success' : 'default'">
                    {{ employee.status === 1 ? t("employees.status.active") : t("employees.status.inactive") }}
                  </v-chip>
                  <v-chip size="x-small" prepend-icon="mdi-account-tie">
                    {{ employee.manager_full_name || 'Fără manager' }}
                  </v-chip>
                  <v-chip
                    v-if="employee.role"
                    size="x-small"
                    prepend-icon="mdi-shield-account"
                  >
                    {{ employee.role }}
                  </v-chip>
                </div>

                <div class="contact-row text-caption">
                  <div>
                    <v-icon size="14" class="mr-1 text-medium-emphasis">mdi-email-outline</v-icon>
                    {{ employee.email || t("employees.labels.notAvailable") }}
                  </div>
                  <div>
                    <v-icon size="14" class="mr-1 text-medium-emphasis">mdi-phone</v-icon>
                    {{ employee.phone || t("employees.labels.notAvailable") }}
                  </div>
                </div>

                <div class="employee-card__actions">
                  <v-btn size="x-small" variant="text" color="primary" @click.stop="goToEmployeeDetails(employee)">
                    {{ t("employees.actions.viewProfile") }}
                  </v-btn>
                  <div class="employee-card__actions-right" v-if="allowManagement">
                    <v-btn size="x-small" variant="text" @click.stop="openEditDialog(employee)">
                      <v-icon size="16" class="mr-1">mdi-pencil</v-icon>
                      {{ t("employees.actions.edit") }}
                    </v-btn>
                    <v-btn
                      size="x-small"
                      variant="text"
                      color="error"
                      @click.stop="confirmDeleteEmployee(employee.id)"
                    >
                      <v-icon size="16" class="mr-1">mdi-delete</v-icon>
                      {{ t("employees.actions.delete") }}
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <div v-else>
          <v-data-table
            :headers="tableHeaders"
            :items="filteredEmployees"
            item-key="id"
            class="employees-table"
            hide-default-footer
          >
            <template #item="{ item }">
              <tr @click="goToEmployeeDetails(item)">
                <td>{{ item.id }}</td>
                <td>{{ item.company_name }}</td>
                <td>{{ item.first_name }}</td>
                <td>{{ item.last_name }}</td>
                <td>{{ item.position }}</td>
                <td>{{ item.manager_full_name || t("employees.labels.notAvailable") }}</td>
                <td>{{ item.role || t("employees.labels.notAvailable") }}</td>
                <td>{{ item.email || t("employees.labels.notAvailable") }}</td>
                <td>
                  <v-chip :color="item.status === 1 ? 'success' : 'default'">
                    {{ item.status === 1 ? t("employees.status.active") : t("employees.status.inactive") }}
                  </v-chip>
                </td>
                <td v-if="allowManagement">
                  <div class="table-actions">
                    <v-btn icon variant="text" size="small" @click.stop="openEditDialog(item)">
                      <v-icon size="18">mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      variant="text"
                      size="small"
                      color="error"
                      @click.stop="confirmDeleteEmployee(item.id)"
                    >
                      <v-icon size="18">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </v-card>

    <v-dialog v-model="editDialog" max-width="640px">
      <v-card>
        <v-card-title class="info-title">
          {{ isEdit ? t("employees.dialog.editTitle") : t("employees.dialog.addTitle") }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form @submit.prevent="isEdit ? updateEmployee() : addEmployee()">
            <v-container class="pa-0">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.company')"
                    :items="companiesData"
                    item-title="company_name"
                    item-value="id"
                    v-model="editEmployee.company_id"
                    :disabled="companyFilter !== null"
                    :rules="[requiredRule]"
                    required
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.firstName')"
                    v-model="editEmployee.first_name"
                    :rules="[requiredRule]"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.lastName')"
                    v-model="editEmployee.last_name"
                    :rules="[requiredRule]"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.birthDate')"
                    v-model="editEmployee.birth_date"
                    type="date"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.hireDate')"
                    v-model="editEmployee.hire_date"
                    type="date"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.releaseDate')"
                    v-model="editEmployee.release_date"
                    type="date"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.status')"
                    :items="statusItemsComputed"
                    v-model="editEmployee.status"
                    item-title="text"
                    item-value="value"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.phone')"
                    v-model="editEmployee.phone"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.email')"
                    v-model="editEmployee.email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.position')"
                    v-model="editEmployee.position"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.role')"
                    :items="roleItemsComputed"
                    v-model="editEmployee.role"
                    item-title="text"
                    item-value="value"
                    clearable
                    :hint="t('employees.fields.roleHint')"
                    persistent-hint
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.manager')"
                    :items="managerOptions"
                    item-title="title"
                    item-value="value"
                    v-model="editEmployee.id_manager"
                    clearable
                    :disabled="!editEmployee.company_id"
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.department')"
                    v-model="editEmployee.department"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.salary')"
                    v-model="editEmployee.salary"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.contractType')"
                    :items="contractTypeItemsComputed"
                    v-model="editEmployee.type_of_contract"
                    item-title="text"
                    item-value="value"
                    clearable
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.address')"
                    v-model="editEmployee.address"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    :label="t('employees.fields.cnp')"
                    v-model="editEmployee.cnp"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.gender')"
                    :items="genderItemsComputed"
                    v-model="editEmployee.gender"
                    item-title="text"
                    item-value="value"
                    clearable
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-combobox
                    :label="t('employees.fields.nationality')"
                    :items="nationalityItemsComputed"
                    v-model="editEmployee.nationality"
                    item-title="text"
                    item-value="value"
                    clearable
                    hide-selected
                  ></v-combobox>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    :label="t('employees.fields.maritalStatus')"
                    :items="maritalStatusItemsComputed"
                    v-model="editEmployee.marital_status"
                    item-title="text"
                    item-value="value"
                    clearable
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <div class="dialog-actions">
                    <v-btn color="primary" type="submit">
                      {{ isEdit ? t('employees.actions.saveChanges') : t('employees.actions.add') }}
                    </v-btn>
                    <v-btn variant="text" @click="closeDialog">{{ t("app.actions.cancel") }}</v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteDialog" max-width="480px">
      <v-card>
        <v-card-title class="info-title">{{ t("employees.delete.title") }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <p class="mb-4">
            {{ t("employees.delete.helper") }}
          </p>
          <v-text-field
            v-model="deleteConfirmInput"
            :label="t('employees.delete.confirmLabel')"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" @click="closeDeleteDialog">{{ t("app.actions.cancel") }}</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            :disabled="deleteConfirmInput !== 'DELETE'"
            @click="performDeleteEmployee"
          >
            {{ t("employees.actions.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="timesheetPrompt.dialog" max-width="460px">
      <v-card>
        <v-card-title class="info-title">{{ t("employees.timesheet.title") }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          {{ t("employees.timesheet.body") }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            variant="text"
            @click="closeTimesheetPrompt"
            :disabled="timesheetPrompt.loading"
          >
            {{ t("app.actions.cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="confirmTimesheetPrompt" :loading="timesheetPrompt.loading">
            {{ t("employees.timesheet.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="successSnackbar"
      color="success"
      :timeout="3000"
      location="bottom"
    >
      {{ successMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
// Importăm DataTable din calea corectă pentru Vuetify 3 stabil:
import { VDataTable } from "vuetify/lib/components/VDataTable";

const normalizeCompanyId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

export default defineComponent({
  name: "Employees",
  components: {
    VDataTable,
  },
  emits: ["back"],
  props: {
    companyIdFilter: {
      type: [Number, String],
      default: null,
    },
    showCompanySelect: {
      type: Boolean,
      default: true,
    },
    autoFetch: {
      type: Boolean,
      default: true,
    },
    allowManagement: {
      type: Boolean,
      default: true,
    },
    openInDashboard: {
      type: Boolean,
      default: false,
    },
    useHrDetailRoute: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    const initialCompanyId = normalizeCompanyId(this.companyIdFilter);

    return {
      employeesData: [],
      companiesData: [],
      editDialog: false,
      isEdit: false,
      selectedCompanyId: initialCompanyId,
      editEmployee: {
        id: null,
        company_id:
          this.companyIdFilter === null || this.companyIdFilter === undefined || this.companyIdFilter === ""
            ? ""
            : Number(this.companyIdFilter),
        first_name: "",
        last_name: "",
        birth_date: "",
        hire_date: "",
        release_date: "",
        status: 1,
        phone: "",
        email: "",
        position: "",
        role: "",
        id_manager: null,
        department: "",
        salary: "",
        type_of_contract: "",
        address: "",
        cnp: "",
        gender: "",
        nationality: "Moldovean",
        marital_status: "",
      },
      statusItems: [
        { text: "", value: 1 },
        { text: "", value: 0 },
      ],
      roleItems: [],
      genderItems: [],
      nationalityItems: [],
      maritalStatusItems: [],
      contractTypeItems: [],
      successSnackbar: false,
      successMessage: "",
      deleteDialog: false,
      timesheetPrompt: {
        dialog: false,
        employeeId: null,
        loading: false,
      },
      defaultTimesheetPayload: {
        use_automatic: true,
        workday_start: "08:00:00",
        workday_end: "17:00:00",
        break_start: "12:00:00",
        break_end: "13:00:00",
        timezone: null,
      },
      deleteTargetId: null,
      deleteConfirmInput: "",
      rules: {},
      search: "",
      viewMode: "cards",
      columns: [],
    };
  },
  computed: {
    translatedColumns() {
      return [
        { key: "id", title: this.t("employees.table.id") },
        { key: "company_name", title: this.t("employees.table.company") },
        { key: "first_name", title: this.t("employees.table.firstName") },
        { key: "last_name", title: this.t("employees.table.lastName") },
        { key: "position", title: this.t("employees.table.position") },
        { key: "manager_full_name", title: this.t("employees.table.manager") },
        { key: "role", title: this.t("employees.table.role") },
        { key: "email", title: this.t("employees.table.email") },
        { key: "status", title: this.t("employees.table.status") },
        { key: "actions", title: this.t("employees.table.actions") },
      ];
    },
    tableHeaders() {
      const headers = this.translatedColumns;
      if (this.allowManagement) {
        return headers;
      }
      return headers.filter((column) => column.key !== "actions");
    },
    translatedStatusItems() {
      return [
        { text: this.t("employees.status.active"), value: 1 },
        { text: this.t("employees.status.inactive"), value: 0 },
      ];
    },
    translatedRoleItems() {
      return [
        { text: this.t("employees.roles.user"), value: "user" },
        { text: this.t("employees.roles.manager"), value: "manager" },
        { text: this.t("employees.roles.director"), value: "director" },
        { text: this.t("employees.roles.hr"), value: "hr" },
      ];
    },
    translatedGenderItems() {
      return [
        { text: this.t("employees.gender.female"), value: "female" },
        { text: this.t("employees.gender.male"), value: "male" },
      ];
    },
    translatedNationalityItems() {
      return [
        { text: this.t("employees.nationality.moldovan"), value: "Moldovean" },
        { text: this.t("employees.nationality.romanian"), value: "Român" },
        { text: this.t("employees.nationality.other"), value: "Altă naționalitate" },
      ];
    },
    translatedMaritalStatusItems() {
      return [
        { text: this.t("employees.maritalStatus.married"), value: "Casatorit" },
        { text: this.t("employees.maritalStatus.single"), value: "Necasatorit" },
      ];
    },
    translatedContractTypeItems() {
      return [
        { text: this.t("employees.contractType.indefinite"), value: "Nedeterminat" },
        { text: this.t("employees.contractType.fixed"), value: "Determinant" },
      ];
    },
    requiredRule() {
      return (value) => !!value || this.t("employees.validation.required");
    },
    statusItemsComputed() {
      return this.translatedStatusItems;
    },
    roleItemsComputed() {
      return this.translatedRoleItems;
    },
    genderItemsComputed() {
      return this.translatedGenderItems;
    },
    nationalityItemsComputed() {
      return this.translatedNationalityItems;
    },
    maritalStatusItemsComputed() {
      return this.translatedMaritalStatusItems;
    },
    contractTypeItemsComputed() {
      return this.translatedContractTypeItems;
    },
    filteredEmployees() {
      const companyFilter = this.companyFilter;
      let list = this.employeesData;
      if (companyFilter !== null) {
        list = list.filter((emp) => Number(emp.company_id) === companyFilter);
      }
      if (!this.search) return list;
      const searchTerm = this.search.toLowerCase();
      return list.filter((emp) =>
        Object.values(emp).some((val) =>
          String(val).toLowerCase().includes(searchTerm)
        )
      );
    },
    companyFilter() {
      return normalizeCompanyId(this.selectedCompanyId);
    },
    activeCompany() {
      if (this.companyFilter === null) {
        return null;
      }
      return (
        this.companiesData.find(
          (company) => Number(company.id) === Number(this.companyFilter)
        ) || null
      );
    },
    companySelectItems() {
      const baseItems = this.companiesData
        .map((company) => ({
          label: company.company_name,
          value: Number(company.id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      if (this.showCompanySelect) {
        return [
          {
            label: this.t("employees.filters.allCompanies"),
            value: null,
          },
          ...baseItems,
        ];
      }
      return baseItems;
    },
    managerOptions() {
      const companyId = Number(this.editEmployee.company_id || this.companyFilter);
      if (!companyId) {
        return [];
      }
      const allowedManagerRoles = ["manager", "director"];
      return this.employeesData
        .filter((emp) => {
          if (Number(emp.company_id) !== companyId) return false;
          if (this.editEmployee.id && emp.id === this.editEmployee.id) return false;
          const role = String(emp.role || "").toLowerCase();
          return allowedManagerRoles.includes(role);
        })
        .map((emp) => ({
          title: `${emp.first_name} ${emp.last_name}`.trim(),
          value: emp.id,
        }));
    },
  },
  watch: {
    companyIdFilter(newVal) {
      if (newVal === undefined) return;
      const normalized = normalizeCompanyId(newVal);
      if (normalizeCompanyId(this.selectedCompanyId) === normalized) {
        return;
      }
      this.selectedCompanyId = normalized;
    },
    companyFilter(newVal, oldVal) {
      if (newVal !== null) {
        this.editEmployee.company_id = newVal;
        this.editEmployee.id_manager = null;
      } else if (!this.isEdit) {
        this.editEmployee.company_id = "";
      }
      if (newVal !== oldVal) {
        const initialRun = oldVal === undefined;
        if (initialRun && !this.autoFetch && (newVal === null || newVal === undefined)) {
          return;
        }
        this.fetchEmployees();
      }
    },
  },
  created() {
    if (this.autoFetch || this.companyFilter !== null) {
      this.fetchEmployees();
    }
    this.fetchCompanies();
    if (this.companyFilter !== null) {
      this.editEmployee.company_id = this.companyFilter;
    }
  },
  methods: {
    handleBack() {
      if (this.openInDashboard) {
        this.$emit("back");
        return;
      }
      if (this.$route?.path?.startsWith("/hr-dashboard")) {
        this.$router.push("/hr-dashboard");
        return;
      }
      this.$router.back();
    },
    employeeInitials(employee) {
      if (!employee) return "?";
      const first = (employee.first_name || "").trim().charAt(0);
      const last = (employee.last_name || "").trim().charAt(0);
      const initials = `${first}${last}`.toUpperCase();
      if (initials) return initials;
      const emailChar = (employee.email || "").trim().charAt(0);
      return emailChar ? emailChar.toUpperCase() : "?";
    },
    fetchCompanies() {
      axios
        .get("/candidati/companies")
        .then((res) => {
          this.companiesData = res.data;
        })
        .catch((err) => {
          console.error("Error fetching companies:", err);
        });
    },
    fetchEmployees() {
      const config = {};
      const filter = this.companyFilter;
      if (filter !== null) {
        config.params = { company_id: filter };
      }

      axios
        .get("/candidati/employees", config)
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : [];
          this.employeesData = list.sort((a, b) => b.id - a.id);
        })
        .catch((err) => {
          console.error("Error fetching employees list:", err);
        });
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === "cards" ? "list" : "cards";
    },
    goToEmployeeDetails(employee) {
      if (!employee?.id) return;
      const detailCompanyId = Number(employee.company_id || this.companyFilter || null);
      if (this.useHrDetailRoute) {
        localStorage.setItem("hr-dashboard:selected-employee-id", String(employee.id));
        if (Number.isFinite(detailCompanyId) && detailCompanyId > 0) {
          localStorage.setItem("hr-dashboard:selected-employee-company-id", String(detailCompanyId));
        } else {
          localStorage.removeItem("hr-dashboard:selected-employee-company-id");
        }
        this.$router.push("/hr-dashboard/employeedetailshr");
        return;
      }
      if (this.openInDashboard) {
        window.dispatchEvent(
          new CustomEvent('ext-dashboard:open-subordinate', {
            detail: {
              employeeId: employee.id,
              companyId: Number.isFinite(detailCompanyId) && detailCompanyId > 0 ? detailCompanyId : null,
            },
          })
        );
        return;
      }
      const targetRoute = this.allowManagement ? "EmployeeDetails" : "EmployeeDetailsExtView";
      this.$router.push({ name: targetRoute, params: { id: employee.id } });
    },
    openEditDialog(employee) {
      this.isEdit = true;
      this.editEmployee = { ...employee };

      function fixDate(dateStr) {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().split("T")[0];
      }
      this.editEmployee.birth_date = fixDate(this.editEmployee.birth_date);
      this.editEmployee.hire_date = fixDate(this.editEmployee.hire_date);
      this.editEmployee.release_date = fixDate(this.editEmployee.release_date);

      if (this.companyFilter !== null) {
        this.editEmployee.company_id = this.companyFilter;
      }

      this.editDialog = true;
    },
    openAddDialog() {
      this.isEdit = false;
      this.editEmployee = {
        id: null,
        company_id: this.companyFilter !== null ? this.companyFilter : "",
        first_name: "",
        last_name: "",
        birth_date: "",
        hire_date: "",
        release_date: "",
        status: 1,
        phone: "",
        email: "",
        position: "",
        role: "",
        id_manager: null,
        department: "",
        salary: "",
        type_of_contract: "",
        address: "",
        cnp: "",
        gender: "",
        nationality: "Moldovean",
        marital_status: "",
      };
      this.editDialog = true;
    },
    addEmployee() {
      const newEmployee = { ...this.editEmployee };
      delete newEmployee.id;

      axios
        .post("/candidati/employees", newEmployee)
        .then((response) => {
          this.successMessage = this.t("employees.messages.addSuccess");
          this.successSnackbar = true;
          this.editDialog = false;
          this.fetchEmployees();
          const newId = response?.data?.id;
          if (newId) {
            this.openTimesheetPrompt(newId);
          }
        })
        .catch((err) => {
          alert(this.t("employees.messages.addError"));
          console.error("Error adding employee:", err);
        });
    },
    updateEmployee() {
      if (!this.editEmployee.id) {
        alert(this.t("employees.messages.missingId"));
        return;
      }

      axios
        .put(`/candidati/employees/${this.editEmployee.id}`, this.editEmployee)
        .then(() => {
          alert(this.t("employees.messages.updateSuccess"));
          this.editDialog = false;
          this.fetchEmployees();
        })
        .catch((err) => {
          console.error("Error updating employee:", err);
        });
    },
    confirmDeleteEmployee(id) {
      this.deleteTargetId = id;
      this.deleteConfirmInput = "";
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteTargetId = null;
      this.deleteConfirmInput = "";
    },
    performDeleteEmployee() {
      if (this.deleteConfirmInput !== "DELETE" || !this.deleteTargetId) return;
      this.deleteEmployee(this.deleteTargetId);
    },
    deleteEmployee(id) {
      axios
        .delete(`/candidati/employees/${id}`)
        .then(() => {
          this.fetchEmployees();
          this.closeDeleteDialog();
        })
        .catch((err) => {
          console.error("Error deleting employee:", err);
        });
    },
    closeDialog() {
      this.editDialog = false;
    },
    openTimesheetPrompt(employeeId) {
      this.timesheetPrompt.employeeId = employeeId;
      this.timesheetPrompt.dialog = true;
      this.timesheetPrompt.loading = false;
    },
    closeTimesheetPrompt() {
      this.timesheetPrompt.dialog = false;
      this.timesheetPrompt.employeeId = null;
      this.timesheetPrompt.loading = false;
    },
    confirmTimesheetPrompt() {
      if (!this.timesheetPrompt.employeeId) {
        this.closeTimesheetPrompt();
        return;
      }
      this.timesheetPrompt.loading = true;
      const payload = { ...this.defaultTimesheetPayload };
      axios
        .put(`/timesheet/settings/${this.timesheetPrompt.employeeId}`, payload)
        .then(() => {
          this.successMessage = this.t("employees.messages.timesheetEnabled");
          this.successSnackbar = true;
          this.closeTimesheetPrompt();
        })
        .catch((err) => {
          console.error("Error enabling automatic timesheet:", err);
          alert(this.t("employees.messages.timesheetError"));
          this.timesheetPrompt.loading = false;
        });
    },
  },
});
</script>

<style scoped>
.employees-module {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-card {
  border-radius: var(--app-radius-base) !important;
}

.info-title {
  font-weight: 600;
  font-size: 16px;
}

.controls-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.company-select {
  max-width: 280px;
  flex: 1 1 220px;
}

.search-field {
  max-width: 320px;
  flex: 1 1 280px;
}
.search-field--full {
  max-width: none;
  flex: 1 1 100%;
}

.employees-grid {
  margin-top: 8px;
}

.employee-card {
  border-radius: var(--app-radius-base);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.employee-card:hover {
  box-shadow: 0 4px 14px rgba(var(--v-theme-on-surface), 0.08);
  transform: translateY(-2px);
}

.employee-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.employee-card__avatar {
  font-weight: 600;
  color: var(--app-text-strong);
}

.employee-card__name {
  font-weight: 600;
  font-size: 17px;
  color: var(--app-text-strong);
}

.employee-card__position {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.employee-card__meta {
  margin-top: 2px;
}

.badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.contact-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  line-height: 1.4;
}

.contact-row div {
  display: flex;
  align-items: center;
  gap: 6px;
}

.contact-row :deep(.v-icon) {
  vertical-align: middle;
}

.employee-card__actions {
  margin-top: auto;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
}

.employee-card__actions-right {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.employee-card__actions .v-btn {
  text-transform: none;
}

.employees-table {
  border-radius: var(--app-radius-base);
  overflow: hidden;
}

.employees-table :deep(th),
.employees-table :deep(td) {
  text-align: left;
}

.table-actions {
  display: flex;
  gap: 4px;
  justify-content: flex-start;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
}
</style>
